'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { ArrowUpRight, Gamepad2, Heart, Maximize2, Sparkles, X } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { claimMedia } from '@/lib/music/media-focus';
import styles from './game-launcher.module.css';

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FullscreenPanel = HTMLDivElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

const GAME_URL = '/games/green-flag-adventure/index.html';

/** Public-site entrance. The game, renderer and soundtrack load only after selection. */
export function GameLauncher({ welcome = false, inline = false }: { welcome?: boolean; inline?: boolean }) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  const [bottom, setBottom] = useState(24);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<FullscreenPanel>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const wasFullscreen = useRef(false);
  const startedFrame = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    setMounted(true);
    if (inline) return;
    // Keep the invitation above privacy choices on narrow screens, without
    // requiring an analytics decision to play or changing the consent banner.
    let observed: Element | null = null;
    let animation = 0;
    const measure = () => {
      animation = 0;
      const banner = document.querySelector('section[aria-label="Privacy choices"]');
      if (observed !== banner) {
        if (observed) resize.unobserve(observed);
        observed = banner;
        if (banner) resize.observe(banner);
      }
      const box = banner?.getBoundingClientRect();
      const button = launcherRef.current?.getBoundingClientRect();
      const overlaps = box && button && box.right > button.left && box.left < button.right;
      setBottom(overlaps ? Math.max(24, window.innerHeight - box.top + 12) : 24);
    };
    const schedule = () => {
      if (!animation) animation = window.requestAnimationFrame(measure);
    };
    const resize = new ResizeObserver(schedule);
    const mutation = new MutationObserver(schedule);
    mutation.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      window.cancelAnimationFrame(animation);
      resize.disconnect();
      mutation.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [inline]);

  const post = useCallback((type: string, extra: Record<string, unknown> = {}) => {
    frameRef.current?.contentWindow?.postMessage({ type: `untangled-game:${type}`, ...extra }, window.location.origin);
  }, []);

  const requestFullscreen = useCallback(async () => {
    const panel = panelRef.current;
    if (!panel) return;
    const doc = document as FullscreenDocument;
    if (doc.fullscreenElement === panel || doc.webkitFullscreenElement === panel) {
      post('pause');
      try {
        if (doc.exitFullscreen) await doc.exitFullscreen();
        else await doc.webkitExitFullscreen?.();
      } catch { /* The edge-to-edge game remains usable if the browser refuses. */ }
      return;
    }
    try {
      if (panel.requestFullscreen) await panel.requestFullscreen();
      else await panel.webkitRequestFullscreen?.();
    } catch {
      // iPhone and embedded browsers may disallow native fullscreen. The same
      // panel already fills the visual viewport and still exposes Exit game.
    }
  }, [post]);

  const close = useCallback(() => {
    post('pause');
    const doc = document as FullscreenDocument;
    if (doc.fullscreenElement === panelRef.current || doc.webkitFullscreenElement === panelRef.current) {
      try {
        const result = doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.();
        void Promise.resolve(result).catch(() => {});
      } catch { /* Removing the fullscreen panel also exits fullscreen. */ }
    }
    // Unmounting, not hiding, tears down audio, WebGL and animation immediately.
    setOpen(false);
    setPlaying(false);
    setReady(false);
    setLoadFailed(false);
    setNativeFullscreen(false);
    wasFullscreen.current = false;
    startedFrame.current = null;
  }, [post]);

  useLayoutEffect(() => {
    if (!open || !playing) return;
    let connected = false;
    const viewChanged = () => {
      const doc = document as FullscreenDocument;
      const active = doc.fullscreenElement === panelRef.current || doc.webkitFullscreenElement === panelRef.current;
      setNativeFullscreen(active);
      post('view', { fullscreen: active });
      if (wasFullscreen.current && !active) post('pause');
      wasFullscreen.current = active;
    };
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow) return;
      const type = event.data?.type;
      if (type === 'untangled-game:ready') {
        connected = true;
        setReady(true);
        setLoadFailed(false);
        viewChanged();
        if (startedFrame.current !== frameRef.current) {
          startedFrame.current = frameRef.current;
          post('start');
          frameRef.current?.contentWindow?.focus();
        }
      } else if (type === 'untangled-game:error') {
        connected = true;
        setLoadFailed(true);
      } else if (type === 'untangled-game:fullscreen') {
        void requestFullscreen();
      }
    };
    const pauseWhenHidden = () => { if (document.hidden) post('pause'); };
    window.addEventListener('message', receive);
    document.addEventListener('fullscreenchange', viewChanged);
    document.addEventListener('webkitfullscreenchange', viewChanged);
    document.addEventListener('visibilitychange', pauseWhenHidden);
    // Safari can dispatch the first message while the iframe's WindowProxy is
    // still changing. Probe the current frame again instead of weakening the
    // exact source/origin checks or depending on a one-shot animation callback.
    let probes = 0;
    const connect = () => { if (!connected) { probes++; post('connect'); } };
    connect();
    const connectionTimer = window.setInterval(() => {
      if (connected || probes >= 60) window.clearInterval(connectionTimer);
      else connect();
    }, 500);
    return () => {
      window.clearInterval(connectionTimer);
      window.removeEventListener('message', receive);
      document.removeEventListener('fullscreenchange', viewChanged);
      document.removeEventListener('webkitfullscreenchange', viewChanged);
      document.removeEventListener('visibilitychange', pauseWhenHidden);
    };
  }, [open, playing, attempt, post, requestFullscreen]);

  useEffect(() => {
    if (!playing || ready || loadFailed) return;
    const timer = window.setTimeout(() => setLoadFailed(true), 30000);
    return () => window.clearTimeout(timer);
  }, [playing, ready, loadFailed, attempt]);

  function selectGame() {
    claimMedia('game');
    startedFrame.current = null;
    // Retain the click's user activation for the Fullscreen API; do not wait
    // for the iframe or its large audio asset before requesting fullscreen.
    flushSync(() => {
      setPlaying(true);
      setReady(false);
      setLoadFailed(false);
    });
    void requestFullscreen();
  }

  function retry() {
    startedFrame.current = null;
    setReady(false);
    setLoadFailed(false);
    setAttempt((value) => value + 1);
  }

  if (!mounted) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(next) => next ? setOpen(true) : close()}>
      <Dialog.Trigger asChild>
        <button ref={launcherRef} type="button" className={inline ? 'music-games' : `${styles.launcher} ${welcome ? styles.welcomeLauncher : ''}`} style={inline ? undefined : { bottom: `max(${bottom}px, var(--play-bottom, 24px), env(safe-area-inset-bottom))` }} aria-label="Games" data-game-launcher>
          <span className={styles.launcherIcon} aria-hidden="true"><Gamepad2 size={23} /></span>
          <span>{inline ? 'Games' : 'Let’s play'}</span>
          {!inline && <Sparkles className={styles.sparkleOne} size={16} aria-hidden="true" />}
          {!inline && <Sparkles className={styles.sparkleTwo} size={12} aria-hidden="true" />}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content ref={panelRef} className={playing ? styles.gameShell : styles.picker} data-game-shell={playing ? '' : undefined} data-game-picker={!playing ? '' : undefined}
          onPointerDownOutside={(event) => { if (playing) event.preventDefault(); }}
          onEscapeKeyDown={(event) => { if (playing) { event.preventDefault(); post('pause'); } }}
          onCloseAutoFocus={(event) => { event.preventDefault(); launcherRef.current?.focus(); }}>
          {playing ? <>
            <div className={styles.gameToolbar}>
              <div className={styles.gameIdentity}><Sparkles size={16} aria-hidden="true" /><Dialog.Title>Green Flag Adventure</Dialog.Title><span>{nativeFullscreen ? 'FULL SCREEN' : 'PLAY TIME'}</span></div>
              <Dialog.Description className={styles.srOnly}>Tap the game to jump. Tap again near the top for a double jump. Use Rainbow to clear obstacles. Escape pauses; Exit game returns to the website.</Dialog.Description>
              <div className={styles.toolbarActions}>
                {!nativeFullscreen && <button type="button" data-game-fullscreen className={styles.screenButton} aria-label="Enter full screen" onClick={() => void requestFullscreen()}><Maximize2 size={17} /></button>}
                <button type="button" data-game-close className={styles.exitButton} onClick={close}><X size={17} aria-hidden="true" /><span>Exit game</span></button>
              </div>
            </div>
            <div className={styles.frameArea}>
              <iframe key={attempt} ref={frameRef} src={GAME_URL} title="Green Flag Adventure game" className={styles.gameFrame} data-game-frame allow="fullscreen; autoplay" allowFullScreen onLoad={() => post('connect')} onError={() => setLoadFailed(true)} />
              {(!ready || loadFailed) && <div className={styles.loading} role="status">
                <Sparkles size={32} aria-hidden="true" />
                <h3>{loadFailed ? 'A little technical hiccup.' : 'A little magic is loading…'}</h3>
                <p>{loadFailed ? 'Check your connection, then try again. You can always return to the website.' : 'Your unicorn, your soundtrack, your moment.'}</p>
                {loadFailed && <button type="button" onClick={retry} className={styles.playButton}>Try again <ArrowUpRight size={18} /></button>}
              </div>}
            </div>
          </> : <>
            <button type="button" data-game-close className={styles.pickerClose} aria-label="Close game picker" onClick={close}><X size={21} /></button>
            <p className={styles.eyebrow}><Sparkles size={14} aria-hidden="true" /> THE UNTANGLED ARCADE</p>
            <Dialog.Title className={styles.title}>A little play.<br /><em>A lot of good energy.</em></Dialog.Title>
            <Dialog.Description className={styles.description}>Take a break. Choose your adventure.</Dialog.Description>
            <div className={styles.gameCard}>
              <div className={styles.cover}>
                <Image src="/games/green-flag-adventure/cover.webp" alt="A pearlescent unicorn in a pastel world of clouds and rainbows" width={960} height={600} sizes="(max-width: 600px) 90vw, 480px" />
                <span className={styles.cardBadge}><Heart size={12} aria-hidden="true" /> CHOOSE YOUR PEACE</span>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardMeta}><span>UNICORN RUNNER</span><span>PHONE + DESKTOP</span></div>
                <h3>Green Flag Adventure</h3>
                <p>Dodge the drama. Collect the good stuff. Turn red flags into rainbows.</p>
                <p className={styles.howToPlay}>Tap anywhere / Space to jump · Rainbow to blast</p>
                <button type="button" className={styles.playButton} data-game-select onClick={selectGame}><Gamepad2 size={19} aria-hidden="true" /><span>Let’s play</span><ArrowUpRight size={20} aria-hidden="true" /></button>
              </div>
            </div>
            <p className={styles.pickerNote}>Free to play · No sign-up · Opens full screen where supported</p>
          </>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
